/**
 * Status validation utilities for staff and site statuses
 * Prevents invalid status values from corrupting dashboard aggregations
 */

// Valid staff status values from existing system
export const VALID_STAFF_STATUSES = new Set([
  'safe',
  'need_assistance', 
  'unaccounted',
  'hospitalised',
  'deceased'
]);

// Valid site status values from existing system
export const VALID_SITE_STATUSES = new Set([
  'operational',
  'partial_operations',
  'non_operational', 
  'evacuated',
  'unknown'
]);

/**
 * Validates and normalizes staff status values
 * @param {string} status - Status to validate
 * @returns {string} Valid status or 'unaccounted' as fallback
 */
export const validateStaffStatus = (status) => {
  if (typeof status !== 'string') return 'unaccounted';
  return VALID_STAFF_STATUSES.has(status) ? status : 'unaccounted';
};

/**
 * Validates and normalizes site status values
 * @param {string} status - Status to validate
 * @returns {string} Valid status or 'unknown' as fallback
 */
export const validateSiteStatus = (status) => {
  if (typeof status !== 'string') return 'unknown';
  return VALID_SITE_STATUSES.has(status) ? status : 'unknown';
};

/**
 * Initialize empty totals object for staff
 * @returns {Object} Empty staff totals
 */
export const getEmptyStaffTotals = () => ({
  safe: 0,
  need_assistance: 0,
  unaccounted: 0,
  hospitalised: 0,
  deceased: 0,
  engaged: 0
});

/**
 * Initialize empty totals object for sites
 * @returns {Object} Empty site totals
 */
export const getEmptySiteTotals = () => ({
  operational: 0,
  partial_operations: 0,
  non_operational: 0,
  evacuated: 0,
  unknown: 0
});

/**
 * Aggregates staff data into totals
 * @param {Array} staffList - Array of staff objects with status property
 * @returns {Object} Aggregated totals
 */
export const aggregateStaffTotals = (staffList) => {
  const totals = getEmptyStaffTotals();
  
  staffList.forEach(staff => {
    const validStatus = validateStaffStatus(staff.status);
    totals[validStatus]++;
    totals.engaged++;
  });
  
  return totals;
};

/**
 * Aggregates site data into totals
 * @param {Array} siteList - Array of site objects with status property
 * @returns {Object} Aggregated totals
 */
export const aggregateSiteTotals = (siteList) => {
  const totals = getEmptySiteTotals();
  
  siteList.forEach(site => {
    const validStatus = validateSiteStatus(site.status);
    totals[validStatus]++;
  });
  
  return totals;
};
